import {formatDate, formatNumber} from '@angular/common';
import {Component, OnDestroy} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {catchError, filter, of, Subscription} from 'rxjs';
import {IPlatformToolComponent, IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommandResponse} from '@shared/commands/command-base';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {adbDrillMeta} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {
  AdminDashboardCompanyInsight,
  AdminDashboardKpi,
  AdminDashboardPoint
} from '@features/admin-panel/models/admin-dashboard.models';

/** The tier as the header badge says it (OCA-E-159 M/attention.ts TIER_LABEL). */
const TIER_LABEL: {[tier: string]: string} = {critical: 'Critical', warning: 'Warning', watch: 'Watch', healthy: 'Healthy'};

/**
 * The Company 360 KPI row (ITP-E-155-02, M/company/[id]/page.tsx:179-262) from data the page already has: the insight
 * (health score and tier, seats, ARR, next renewal), its 12 weekly login totals, and the timeline part's daily health
 * scores. Logins compare the last 4 weeks with the 4 before. `secondaryText` is what the tile (ITP-E-149) shows as the
 * unit beside the value: the tier, logins per seat, "of 10 · 70%", the renewal day. Empty without an insight.
 */
export function c360Kpis(data: AdminDashboardCompanyInsight | null, health: AdminDashboardPoint[]): AdminDashboardKpi[] {
  const insight = data?.Insight;
  if (!insight) {
    return [];
  }
  const weekly = data.WeeklyLogins ?? [];
  const sum = (points: AdminDashboardPoint[]): number => points.reduce((total, point) => total + (point.value ?? 0), 0);
  const last4 = weekly.slice(-4);
  const before4 = weekly.length >= 8 ? weekly.slice(-8, -4) : [];
  const seats = insight.signals?.seats ?? 0;
  const used = insight.signals?.seatsUsed ?? 0;
  const renewal = insight.signals?.nextRenewal;
  const tile = (kpi: Partial<AdminDashboardKpi>): AdminDashboardKpi => ({
    key: '', label: '', unit: 'count', value: null, total: null, previousValue: null, secondaryText: null,
    series: [], historyFrom: null, notApplicable: false, ...kpi
  });
  return [
    tile({
      key: 'c360-health', label: 'Health score', value: insight.healthScore,
      secondaryText: insight.healthScore === null ? null : TIER_LABEL[insight.tier || 'healthy'] ?? null,
      series: health ?? []
    }),
    tile({
      key: 'c360-logins', label: 'Logins, last 4 weeks', value: last4.length ? sum(last4) : null,
      previousValue: before4.length ? sum(before4) : null,
      secondaryText: last4.length && seats ? `${formatNumber(Math.round(sum(last4) / seats), 'en-US', '1.0-0')} per seat` : null,
      series: weekly
    }),
    tile({
      key: 'c360-seats', label: 'Seats used', value: used,
      secondaryText: seats ? `of ${formatNumber(seats, 'en-US', '1.0-0')} · ${Math.round(used / seats * 100)}%` : 'No licensed seats'
    }),
    tile({
      key: 'c360-arr', label: 'ARR', unit: 'usd', value: insight.isClient ? insight.arr : null,
      notApplicable: !insight.isClient,
      secondaryText: insight.isClient && renewal ? `Renews ${formatDate(renewal, 'd MMM y', 'en-US', 'UTC')}` : null
    })
  ];
}

/** A licence status worth saying in the meta line: 'N/A' and blanks say nothing. */
function c360Status(status: string | null | undefined): string | null {
  const value = (status ?? '').trim();
  return value && value !== 'N/A' ? value : null;
}

/**
 * The Company 360 meta line (ITP-E-166-02; OCA-E-159 M/company/[id]/page.tsx:330-333 with Plan ITP-P-35 Q2: owner and
 * seats instead of country and size, worded by the shared drill meta line `adbDrillMeta`, ITP-E-159-02). A client:
 * "acme · Active · KPMG · Maya Lindqvist · 12 seats · customer since 12 Mar 2024"; a company not in Client Manager: "acme · Not in Client Manager · Self · customer since 15 Jan
 * 2025". Each missing part is left out. Null when the response carries neither `Meta` (ITP-E-165) nor `Profile`
 * (ITP-E-166-01): the page then keeps the R4 line ("acme · Client · as of 3 Oct 2026").
 */
export function c360MetaLine(companyId: string | null, data: AdminDashboardCompanyInsight | null): string | null {
  const meta = data?.Meta ?? null;
  const profile = data?.Profile ?? null;
  if (!meta && !profile) {
    return null;
  }
  const isClient = meta ? meta.isClient === true : data?.Insight?.isClient === true;
  const since = profile?.customerSince
    ? `customer since ${formatDate(profile.customerSince, 'd MMM y', 'en-US', 'UTC')}`
    : null;
  const source = (profile?.source ?? '').trim() || null;
  const parts = isClient
    ? [
      companyId,
      c360Status(profile?.licenceStatus),
      source,
      meta?.ownerName
        ? adbDrillMeta({companyId: meta.companyId, companyName: meta.companyName, ownerName: meta.ownerName, seats: meta.seats ?? null})
        : null,
      since
    ]
    : [companyId, 'Not in Client Manager', source, since];
  return parts.filter(part => !!part).join(' · ');
}

/** The Company 360 page ('/admin-panel/company-360/:companyId'): one company's health, engagement, commercial and product use. */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-company-360',
  templateUrl: './admin-dashboard-company-360.component.html',
  styleUrls: ['./admin-dashboard-company-360.component.scss']
})
export class AdminDashboardCompany360Component implements IPlatformToolComponent, OnDestroy {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  companyId: string | null = null;
  data: AdminDashboardCompanyInsight | null = null;
  /** The four KPI tiles under the header (ITP-E-155-02), rebuilt when the insight or the health history arrives. */
  kpis: AdminDashboardKpi[] = [];
  /** The ITP-E-166-02 meta line; null keeps the R4 line ("acme · Client · as of 3 Oct 2026"). */
  metaLine: string | null = null;

  /** The Copy link button's label: 'Copy link', then 'Link copied' for 2 s after a copy (ITP-E-166-02). */
  copyLabel = 'Copy link';

  /** Writes text to the clipboard with navigator.clipboard.writeText (rejects where there is none); a spec replaces it. */
  writeClipboard: (text: string) => Promise<void> = text =>
    typeof navigator !== 'undefined' && navigator.clipboard?.writeText
      ? navigator.clipboard.writeText(text)
      : Promise.reject(new Error('No clipboard'));

  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  /** Puts this page's address on the clipboard; the label says 'Link copied' for 2 s. A refused copy changes nothing. */
  copyLink(): void {
    this.writeClipboard(window.location.href).then(() => {
      this.copyLabel = 'Link copied';
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(() => {
        this.copyLabel = 'Copy link';
        this.copyTimer = null;
      }, 2000);
    }, () => undefined);
  }

  granted: (feature: string) => boolean = () => false;

  private featureSubscription: Subscription;
  private healthSeries: AdminDashboardPoint[] = [];

  constructor(private dataService: AdminDashboardDataService, private store: Store) {
    // No take(1): hasFeature emits a function before the user's features have loaded, so every later emission
    // re-grants. Unsubscribed in ngOnDestroy.
    this.featureSubscription = this.store.pipe(select(hasFeature), filter(fn => !!fn))
      .subscribe(fn => this.granted = feature => fn(feature) === true);
  }

  ngOnDestroy(): void {
    this.featureSubscription?.unsubscribe();
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
  }

  onLoad(): void {
    this.companyId = this.selectedTab?.tabData?.['CompanyId'] ?? null;
    this.dataService.getCompanyInsight(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyInsight))
    ).subscribe(data => {
      this.data = data;
      this.metaLine = c360MetaLine(this.companyId, data);
      this.kpis = c360Kpis(data, this.healthSeries);
    });
  }

  /** The timeline part's daily health scores (its `healthLoaded` output): the Health score tile's sparkline. */
  onHealthLoaded(health: AdminDashboardPoint[]): void {
    this.healthSeries = health ?? [];
    this.kpis = c360Kpis(this.data, this.healthSeries);
  }

  /** "Critical", "Warning", "Watch" or "Healthy" for the header badge ("Watch · 72"). */
  tierLabel(tier: string | null): string {
    return TIER_LABEL[tier || 'healthy'] ?? tier;
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }
}
